import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@finitless/shared/lib";
import { isEndpointEnabled } from "@finitless/shared/config";

/**
 * Hook to get third party integrations
 * @returns {Object} Query result with integrations
 */
export const useThirdPartyIntegrations = () => {
  return useQuery({
    queryKey: ["thirdPartyIntegrations"],
    queryFn: async () => {
      const response = await apiClient.get("/third_party_integrations");
      return {
        integrations: response?.result || [],
        pagination: response?.pagination || {},
      };
    },
    enabled: isEndpointEnabled("thirdPartyIntegrations", "list"),
    staleTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: false,
    retry: false,
  });
};

/**
 * Get POS integrations from third party integrations
 * @param {Array} integrations - Array of integrations
 * @returns {Array} Filtered POS integrations
 */
export const getPOSIntegrations = (integrations = [], business) => {
  const isIntegrationConnected = (integration) => {
    if (
      !business?.third_party_integrations ||
      !Array.isArray(business.third_party_integrations)
    ) {
      return false;
    }

    return business.third_party_integrations.some(
      (item) => item?.third_party_integration_id === integration?.id,
    );
  };
  const list = integrations.filter(
    (integration) =>
      integration.type === "pos" && isIntegrationConnected(integration),
  );
  return list.length
    ? list
    : integrations.filter(
        (integration) => integration.type === "pos" && integration?.plugin_id,
      );
};

/**
 * Get business POS integrations
 * @param {Object} business - Business object
 * @returns {Array} Business POS integrations with setup status
 */
export const getBusinessPOSIntegrations = (business) => {
  if (!business?.third_party_integrations) return [];

  return business.third_party_integrations
    .filter(
      (integration) => integration.third_party_integration?.type === "pos",
    )
    .map((integration) => ({
      ...integration.third_party_integration,
      setup_status: integration.setup_status,
      enabled: integration.enabled,
    }));
};

/**
 * Get Sales Channel integrations from third party integrations
 * @param {Array} integrations - Array of integrations
 * @returns {Array} Filtered Sales Channel integrations
 */
export const getSalesChannelIntegrations = (integrations = [], business) => {
  const isIntegrationConnected = (integration) => {
    if (
      !business?.third_party_integrations ||
      !Array.isArray(business.third_party_integrations)
    ) {
      return false;
    }

    return business.third_party_integrations.some(
      (item) => item?.third_party_integration_id === integration?.id,
    );
  };
  const list = integrations.filter(
    (integration) =>
      integration.type === "sales_channel" &&
      isIntegrationConnected(integration),
  );
  return list.length
    ? list
    : integrations.filter(
        (integration) =>
          integration.type === "sales_channel" && integration?.plugin_id,
      );
};

/**
 * Get business Sales Channel integrations
 * @param {Object} business - Business object
 * @returns {Array} Business Sales Channel integrations with setup status
 */
export const getBusinessSalesChannelIntegrations = (business) => {
  if (!business?.third_party_integrations) return [];

  return business.third_party_integrations
    .filter(
      (integration) =>
        integration.third_party_integration?.type === "sales_channel",
    )
    .map((integration) => ({
      ...integration.third_party_integration,
      setup_status: integration.setup_status,
      enabled: integration.enabled,
    }));
};

export default {
  useThirdPartyIntegrations,
  getPOSIntegrations,
  getBusinessPOSIntegrations,
  getSalesChannelIntegrations,
  getBusinessSalesChannelIntegrations,
};
